import * as React from 'react'
import * as XLSX from 'xlsx'
import * as Papa from 'papaparse'
import { CurrentCompanyStore } from '../../../../../../stores/CurrentCompanyStore'
import { axios, ONPeriodicReport } from '@getgreenline/homi-shared'
import { FormError } from '../../../../../../components/FormError'
import { ComplianceStore } from '../../ComplianceStore'
import { numberPadder, parseErrorMsg } from '../../../../../../utilities/helpers'
import { Button } from 'antd'
import moment from 'moment'
import { Track } from '../../../../../../Track'
import { ONInventoryAdjustmentsReport, ONDestructionsReport, ONTransfersReport } from './ONReports'

export type ONPeriodicReportType =
  | 'Inventory-Adjustments'
  | 'Destructions'
  | 'Transfer-In'
  | 'Transfer-Out'

interface Props {
  currentCompanyStore: CurrentCompanyStore
  complianceStore: ComplianceStore
  nameOfStore?: string
  retailStoreAuthorization: string
  reportType: ONPeriodicReportType
  reportName: string
}

interface State {
  isLoading: boolean
  errorMessage?: string
}

export class OntarioPeriodicReport extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      isLoading: false,
    }
  }

  parseAndCreateExcelWorkbook = (csvString: string) => {
    const parsedData = Papa.parse(csvString, {
      skipEmptyLines: true,
    })

    const data = this.parseReportData(this.props.reportType, parsedData.data)

    const dataRows = [parsedData.data[0], ...data]

    /* convert from array of arrays to workbook */
    const inventoryAndSales = XLSX.utils.aoa_to_sheet(dataRows)

    return inventoryAndSales
  }

  parseReportData = (reportType: ONPeriodicReportType, data: any[]) => {
    const dataRows = data.slice(1, data.length)

    switch (reportType) {
      case ONPeriodicReport.INVENTORY_ADJUSTMENTS:
        return dataRows.map((data) => Object.values(new ONInventoryAdjustmentsReport(data)))
      case ONPeriodicReport.DESTRUCTIONS:
        return dataRows.map((data) => Object.values(new ONDestructionsReport(data)))
      case ONPeriodicReport.INTERSTORE_TRANSFER_IN:
        return dataRows.map((data) => Object.values(new ONTransfersReport(data)))
      case ONPeriodicReport.INTERSTORE_TRANSFER_OUT:
        return dataRows.map((data) => Object.values(new ONTransfersReport(data)))
      default:
        return []
    }
  }

  generatePeriodicReport = async () => {
    const { locationIds, month, year } = this.props.complianceStore
    const { reportType } = this.props
    const reportStartDate = moment()
      .month(month - 1)
      .year(year)
      .startOf('month')
    const reportEndDate = moment()
      .month(month - 1)
      .year(year)
      .endOf('month')

    const params = {
      startDate: reportStartDate.clone().format('YYYY-MM-DD'),
      endDate: reportEndDate.clone().format('YYYY-MM-DD'),
      locationIds,
    }

    Track.track('dashboard:compliance_complianceReportGenerated', params)

    const company = this.props.currentCompanyStore.company!
    this.setState({
      isLoading: true,
      errorMessage: undefined,
    })

    try {
      const response = await axios.get(
        `/api/v1/company/${company.id}/reports/compliance/periodic/${reportType}`,
        {
          params: params,
        },
      )

      const periodicReports = response.data
      const report = this.parseAndCreateExcelWorkbook(periodicReports)
      const new_workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(new_workbook, report, reportType)

      if (!reportEndDate) {
        return
      }
      const fileMonth = numberPadder(reportEndDate.clone().toDate().getMonth() + 1, 2)
      const fileYear = reportEndDate.clone().toDate().getFullYear().toString().substr(-2)

      const title = `${company.name}_CRSA#${this.props.retailStoreAuthorization}_AGCO-${reportType}_${fileMonth}${fileYear}.xlsx`
      XLSX.writeFile(new_workbook, title)
    } catch (error) {
      this.setState({
        errorMessage: parseErrorMsg(error),
      })
    } finally {
      this.setState({ isLoading: false })
    }
  }

  render() {
    const { isLoading, errorMessage } = this.state
    const { locationIds } = this.props.complianceStore
    const { reportName } = this.props

    return (
      <div>
        <h5>{this.props.reportName} report</h5>

        <Button
          type='primary'
          onClick={this.generatePeriodicReport}
          loading={locationIds.length === 0 || isLoading}
        >
          {isLoading ? 'Downloading...' : `Download ${reportName.toLowerCase()} report`}
        </Button>
        <FormError message={errorMessage} />
      </div>
    )
  }
}
